import {
  ApolloClient,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  ObservableQuery,
  split,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import fetch from 'cross-fetch';
import merge from 'deepmerge';
import { Kind, OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { IncomingHttpHeaders } from 'http';
import { isEqual } from 'lodash';
import { AppProps } from 'next/app';
import { PostHog } from 'posthog-js';
import { usePostHog } from 'posthog-js/react';
import { useMemo } from 'react';

import PossibleTypesJSON from '@/__generated__/possibleTypes.json';
import { getAppVersion } from '@/utils/environmentUtils';
import { getPosthogSessionURL } from '@/utils/posthogUtils';

import {
  annotateGraphQLErrorsLink,
  forceLoginLink,
  logErrorDetailsLink,
  printApolloErrorsMonitor,
  printNetworkStatusMonitor,
  printSubscriptionBlocker,
  reportNetworkErrorsLink,
  scalarParsingLink,
} from './apolloLinks';
import { getGraphqlEndpoint, shouldRefetchQuery } from './client.utils';
import {
  connectionHealthListeners,
  observeConnectionPageLifecycle,
} from './connectionHealth';
import { debugHeaderLink } from './debugHeaderLink';
import { mockFamilyTreeLink } from './mockFamilyTreeLink';
import { createMutationConsistencyLink } from './mutationConsistencyLink';
import { mergedTypePolicies } from './typePolicies/mergedTypePolicies';

let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;

function createApolloClient(
  posthogClient: PostHog | null, // null in SSR
  headers: IncomingHttpHeaders | null = null
) {
  const passHeadersToFetch: typeof fetch = (url, init) => {
    const cookies: Record<string, string> = {};

    if (headers?.cookie) {
      cookies.Cookie = headers.cookie;
    }

    return fetch(url, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        ...cookies,
      },
    }).then((response) => response);
  };

  const wsLinkUrl = getGraphqlEndpoint(headers, { isWebSocket: true });
  const httpLinkUri = getGraphqlEndpoint(headers, { isWebSocket: false });
  observeConnectionPageLifecycle();

  // graphql-ws 6.0.7 shares error/close listeners across sockets. If a
  // consumer opens a new subscription during a socket's native error event,
  // the old socket's close rejects the new connection and leaks an unhandled
  // CloseEvent rejection (INTERFACE-10B8). Consumers that restart on transport
  // errors must defer the restart to the next task; see
  // HouseholdChat.provider.tsx onError for this per-consumer workaround.
  const wsLink = new GraphQLWsLink(
    createClient({
      url: wsLinkUrl,
      on: typeof window === 'undefined' ? undefined : connectionHealthListeners,
    })
  );

  const customHeaders: Record<string, string> = {};
  if (posthogClient) {
    const posthogSessionUrl = getPosthogSessionURL(posthogClient, {
      withTimestamp: true,
    });
    if (posthogSessionUrl) {
      customHeaders['x-lum-replay-url'] = posthogSessionUrl;
    }
  }

  const terminalLink = createUploadLink({
    uri: httpLinkUri,
    includeExtensions: true,
    fetchOptions: {
      mode: 'cors',
    },
    headers: customHeaders,
    credentials: 'same-origin',
    fetch: passHeadersToFetch,
  });
  const mutationConsistencyLink = createMutationConsistencyLink();

  // split based on operation type -- if a subscription, open a websocket connection; otherwise, send a standard HTTP request
  // https://www.apollographql.com/docs/react/data/subscriptions/#3-split-communication-by-operation-recommended
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === Kind.OPERATION_DEFINITION &&
        definition.operation === OperationTypeNode.SUBSCRIPTION
      );
    },
    wsLink,
    terminalLink
  );

  return new ApolloClient({
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all', // For refetched queries, return partial data if there are errors
      },
      mutate: {
        onQueryUpdated: (query: ObservableQuery<unknown>) => {
          // Prevent some queries from refetching automatically
          return shouldRefetchQuery(query.queryName);
        },
      },
    },
    ssrMode: typeof window === 'undefined',
    connectToDevTools: true, // https://www.apollographql.com/docs/react/development-testing/developer-tooling/
    link: from([
      mockFamilyTreeLink, // Intercept queries with mock data when ?familyTreeMock=N is in URL
      printSubscriptionBlocker,
      printNetworkStatusMonitor,
      printApolloErrorsMonitor,
      debugHeaderLink,
      forceLoginLink,
      reportNetworkErrorsLink,
      logErrorDetailsLink,
      annotateGraphQLErrorsLink,
      scalarParsingLink,
      mutationConsistencyLink,
      splitLink,
    ]),
    cache: new InMemoryCache({
      typePolicies: mergedTypePolicies,
      possibleTypes: PossibleTypesJSON.possibleTypes,
    }),
    name: 'WebClient',
    version: getAppVersion(),
  });
}

export const APOLLO_STATE_PROP_NAME = '__APOLLO_STATE__';

export function useApollo(pageProps: AppProps['pageProps']) {
  const posthogClient = usePostHog();
  const state = pageProps[APOLLO_STATE_PROP_NAME];

  return useMemo(() => {
    return initializeApollo(posthogClient, { initialState: state });
  }, [posthogClient, state]);
}

export function addApolloState(
  client: ApolloClient<NormalizedCacheObject>,
  pageProps: AppProps<{
    props: Record<string, unknown>;
  }>['pageProps']
) {
  if (pageProps?.props) {
    pageProps.props[APOLLO_STATE_PROP_NAME] = client.cache.extract();
  }

  return pageProps;
}

type InitialState = NormalizedCacheObject | undefined;

interface InitializeApollo {
  headers?: IncomingHttpHeaders | null;
  initialState?: InitialState | null;
}

export function initializeApollo(
  posthogClient: PostHog | null, // null in SSR
  { headers, initialState }: InitializeApollo = {
    headers: null,
    initialState: null,
  }
) {
  const client = apolloClient ?? createApolloClient(posthogClient, headers);

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = client.extract();

    // Merge the existing cache into data passed from getStaticProps/getServerSideProps
    // This pattern is from the official Apollo Next.js example:
    // https://github.com/apollographql/next-apollo-example/blob/main/lib/apolloClient.js
    const data = merge(existingCache, initialState, {
      // combine arrays using object equality (like in sets)
      arrayMerge: (destinationArray: unknown[], sourceArray: unknown[]) => [
        ...sourceArray,
        ...destinationArray.filter((d) =>
          sourceArray.every((s: unknown) => !isEqual(d, s))
        ),
      ],
    });

    // Restore the cache with the merged data
    client.cache.restore(data);
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return client;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = client;

  return client;
}
